'use client';

import * as React from 'react';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { IconChevronRight } from '@tabler/icons-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Icons } from '@/assets/icons';
import { useSidebar } from '@/shared/providers/sidebar';
import { cn } from '@/shared/utils/style/cn';
import { Slot } from '@radix-ui/react-slot';
import CloseIcon from '@/shared/components/close-icon';
import { Button } from '@/shared/components/button';
import { Img } from '@/shared/components/image';
import { useGetFixMainProduct } from '@/domain/product/query/use-get-fix-main-product';
import { getNavItems, ProductNavItem } from '@/shared/utils/nav/get-nav-items';

export default function Sidebar() {
  const pathname = usePathname();
  const { toggleSidebar } = useSidebar();
  const { data } = useGetFixMainProduct();

  const products = data.data ?? [];
  const navItems = getNavItems(products);

  return (
    <SidebarWrap>
      <SidebarHeader />
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {navItems.map((item) => {
              const firstPathName = '/' + pathname.split('/')[1];
              return item?.items && item?.items?.length > 0 ? (
                <CollapsibleMenuItem
                  key={item.title}
                  asChild
                  defaultOpen={item.isActive}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        isActive={item.items?.map((e) => e.url).includes(firstPathName)}
                      >
                        <span>{item.title}</span>
                        <IconChevronRight className="ml-auto rotate-90 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-270" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <ProductList>
                        {item.items?.map((subItem: ProductNavItem) => (
                          <ProductListItem
                            key={subItem.title}
                            style={{ background: subItem.productLineBackgroundColor }}
                          >
                            <ProductItemLinkButton asChild>
                              <Link
                                href={subItem.url}
                                onClick={toggleSidebar}
                                className={'flex gap-[3px]'}
                              >
                                <Img
                                  src={subItem.url}
                                  width={33}
                                  height={33}
                                  alt={'product-thumbnail-image shrink-0'}
                                />
                                <span className={'line-clamp-2 text-ellipsis'}>
                                  {subItem.title}
                                </span>
                              </Link>
                            </ProductItemLinkButton>
                          </ProductListItem>
                        ))}
                      </ProductList>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </CollapsibleMenuItem>
              ) : (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={firstPathName === item.url}>
                    <Link href={item.url} onClick={toggleSidebar}>
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
    </SidebarWrap>
  );
}

function SidebarWrap({ children, ...props }: React.ComponentProps<'div'>) {
  const { state } = useSidebar();

  return (
    <div
      className="group peer text-sidebar-foreground transition-all ease-linear"
      data-state={state}
      data-collapsible={state === 'collapsed' ? 'offcanvas' : ''}
      data-variant="sidebar"
      data-side="left"
      data-slot="sidebar"
    >
      <div
        data-slot="sidebar-container"
        className={cn(
          'absolute inset-y-0 left-0 z-10 h-svh w-(--sidebar-width) bg-white transition-all duration-150 ease-in-out',
          'group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]',
          'shadow-[4px_0px_10px_0px_rgba(0,0,0,0.1)]',
          'group-data-[collapsible=offcanvas]:opacity-0 group-data-[collapsible=offcanvas]:shadow-none',
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="bg-sidebar flex h-full w-full flex-col"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function SidebarHeader({ ...props }: React.ComponentProps<'div'>) {
  const { toggleSidebar } = useSidebar();

  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn(
        'text-gray90 flex h-[48px] items-center gap-[8px] px-[16px] py-[12px] [&_svg]:shrink-0',
      )}
      {...props}
    >
      <CloseIcon
        width={24}
        height={24}
        strokeWidth={2}
        className="cursor-pointer"
        onClick={toggleSidebar}
      />
      <Icons.MediPro width={138} height={24} />
    </div>
  );
}

function SidebarContent({ ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn('flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto py-[32px]')}
      {...props}
    />
  );
}

function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn('relative flex w-full min-w-0 flex-col px-[32px]', className)}
      {...props}
    />
  );
}

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn('flex w-full min-w-0 flex-col gap-[24px]', className)}
      {...props}
    />
  );
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn('group/menu-item relative', className)}
      {...props}
    />
  );
}

function CollapsibleMenuItem({ ...props }: React.ComponentProps<typeof CollapsiblePrimitive.Root>) {
  return <CollapsiblePrimitive.Root data-slot="collapsible" {...props} />;
}

function CollapsibleTrigger({
  ...props
}: React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleTrigger>) {
  return <CollapsiblePrimitive.CollapsibleTrigger data-slot="collapsible-trigger" {...props} />;
}

function CollapsibleContent({
  ...props
}: React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleContent>) {
  return <CollapsiblePrimitive.CollapsibleContent data-slot="collapsible-content" {...props} />;
}

const sidebarMenuButtonClassName = cn(
  'flex h-[44px] w-full cursor-pointer items-center gap-[16px] overflow-hidden pb-[20px] text-left outline-hidden transition-all [&>span]:whitespace-nowrap [&>svg]:size-[14px] [&>svg]:shrink-0 [&>svg]:break-keep',
  'border-gray10 border-b',
  // 해당 항목 열렸을때(PRODUCT에서 사용)
  'data-[state=open]:text-pink150 data-[state=open]:border-none',
  // active(clicked)
  'active:border-gray20 typo-heading-2 active:font-semibold',
  // active(active page)
  'data-[active=true]:border-pink150 data-[active=true]:text-pink150 data-[active=true]:border-b data-[active=true]:font-semibold',
);

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  isActive?: boolean;
}) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-active={isActive}
      className={cn(sidebarMenuButtonClassName, 'peer/menu-button typo-body-2')}
      {...props}
    />
  );
}

function ProductList({ ...props }: React.ComponentProps<'ul'>) {
  const { toggleSidebar } = useSidebar();
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn('grid min-w-0 translate-x-px grid-cols-2 flex-col gap-x-[4px] gap-y-[8px]')}
      {...props}
    >
      {props.children}
      <div className="col-span-full">
        <Link href={'/product'}>
          <Button
            $variant="outline-secondary"
            $size="sm"
            className="mt-[12px] w-full"
            onClick={toggleSidebar}
          >
            View all
          </Button>
        </Link>
      </div>
    </ul>
  );
}

function ProductListItem({ ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn(
        'group/menu-sub-item bg-pink10 typo-caption-2 relative rounded-[12px] px-[6px] py-[8px]',
      )}
      {...props}
    />
  );
}

function ProductItemLinkButton({
  asChild = false,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean;
}) {
  const Comp = asChild ? Slot : 'a';
  return <Comp data-slot="sidebar-menu-sub-button" data-sidebar="menu-sub-button" {...props} />;
}
